/**
 * Selecionador dos 5 números executivos (T20).
 *
 * Escolhe até 5 highlights priorizando: score, custo, retrabalho, risco
 * e visibilidade. Pula custo financeiro quando MoneyEstimate é null.
 * Pula turnover se dados complementares não foram carregados.
 */

import type { DiagnosticResult } from '../diagnostic.types';
import type { Metric, MoneyEstimate, PercentageMetric } from '../metrics/metrics.types';
import type { ExecutiveHighlight } from './report.types';
import { formatScore, formatPercentage, formatCurrencyBrl } from '../format/numberFormat';
import { calcPeriodDays } from '../metrics/costMetrics';

// =========================================================================
// Helpers de formatação
// =========================================================================

/**
 * Formata valor de uma métrica para exibição amigável.
 * Aceita qualquer Metric que possua kind e value.
 */
export function formatHighlightValue(metric: Metric & { kind: string; value?: number }): string {
  const value = (metric as { value?: number }).value;

  if (metric.kind === 'money') {
    const money = metric as MoneyEstimate;
    return formatCurrencyBrl(money.value);
  }

  if (metric.kind === 'percentage') {
    const pct = metric as PercentageMetric;
    return formatPercentage(pct.value);
  }

  if (value !== undefined) {
    return String(value);
  }

  return '';
}

/**
 * Score de prioridade para ordenação baseado no código da métrica.
 * Maior = mais importante. Garante que score seja sempre o primeiro.
 */
export function getHighlightPriority(metric: Metric): number {
  const code = metric.code;
  if (code.startsWith('score')) return 100;
  if (code.startsWith('cost')) return 80;
  if (code.startsWith('rework') || code.startsWith('rw')) return 60;
  if (code.startsWith('risk')) return 40;
  if (code.startsWith('visibility') || code.startsWith('vis') || code.startsWith('v.')) return 20;
  return 10;
}

/** Score de prioridade para ordenação de highlights por categoria. */
function highlightPriority(highlight: ExecutiveHighlight): number {
  const ORDER: Record<ExecutiveHighlight['category'], number> = {
    score: 100,
    cost: 80,
    rework: 60,
    risk: 40,
    visibility: 20,
  };
  return ORDER[highlight.category] ?? 0;
}

// =========================================================================
// Construção dos highlights por categoria
// =========================================================================

function buildScoreHighlight(result: DiagnosticResult): ExecutiveHighlight {
  const { score } = result;
  const bandLabel: Record<string, string> = {
    critico: 'Crítico',
    atencao: 'Atenção',
    consistente: 'Consistente',
    exemplar: 'Exemplar',
  };
  return {
    code: 'score.maturity',
    label: 'Score de maturidade',
    formattedValue: `${formatScore(score.total)}/100`,
    explanation: `Maturidade de jornada classificada como "${bandLabel[score.band] ?? score.band}" com base em 5 dimensões ponderadas.`,
    category: 'score',
  };
}

function buildCostHighlight(result: DiagnosticResult): ExecutiveHighlight | null {
  // Usa costTotal (soma de horas extras + jornadas mais curtas + faltas +
  // correções) quando disponível pra bater com a seção de custos.
  // Cai pra estimatedExcessCost (só horas extras) como compatibilidade
  // quando algum componente não pôde ser calculado.
  const { costTotal, estimatedExcessCost } = result.metrics.cost;
  const source = costTotal ?? estimatedExcessCost;
  if (source === null) return null;

  const periodDays = calcPeriodDays(
    result.dataset.period.start,
    result.dataset.period.end,
  );
  const monthlyEquivalent = (source.value / periodDays) * 30;

  const totalFormatted = formatCurrencyBrl(source.value);
  const monthlyFormatted = formatCurrencyBrl(monthlyEquivalent);
  const formattedValue = `${totalFormatted} (≈ ${monthlyFormatted}/mês)`;

  const assumptionParts = source.assumptionsUsed ?? [];
  const isTotal = costTotal !== null;

  return {
    code: isTotal ? 'cost.total' : 'cost.estimated_excess',
    label: isTotal ? 'Custo total estimado' : 'Custo potencial de excesso',
    formattedValue,
    explanation: isTotal
      ? `Soma de horas extras, jornadas mais curtas, faltas e correções no período analisado (${periodDays} dias). Mensal extrapolado para 30 dias.`
      : `Custo potencial dos minutos de excesso no período analisado (${periodDays} dias). Mensal extrapolado para 30 dias.`,
    assumption: assumptionParts.length > 0 ? assumptionParts[0] : undefined,
    category: 'cost',
  };
}

function buildCostPerEmployeeHighlight(
  result: DiagnosticResult,
): ExecutiveHighlight | null {
  const { costTotalPerEmployee } = result.metrics.cost;
  if (costTotalPerEmployee === null) return null;

  const periodDays = calcPeriodDays(
    result.dataset.period.start,
    result.dataset.period.end,
  );
  const monthlyEquivalent = (costTotalPerEmployee.value / periodDays) * 30;

  const periodFormatted = formatCurrencyBrl(costTotalPerEmployee.value);
  const monthlyFormatted = formatCurrencyBrl(monthlyEquivalent);

  // Mostra a média mensal em destaque (mais acionável pra o consultor) e o
  // valor do período entre parênteses como contexto.
  const formattedValue = `${monthlyFormatted}/mês (≈ ${periodFormatted} no período)`;

  return {
    code: 'cost.total_per_employee_monthly',
    label: 'Custo médio mensal por colaborador',
    formattedValue,
    explanation: `Custo total estimado dividido pelos colaboradores ativos, extrapolado para 30 dias (período analisado: ${periodDays} dias).`,
    category: 'cost',
  };
}

/**
 * Custo INFORMATIVO de blocos de afastamento (atestado/licença/férias).
 * NÃO é recuperável — é benefício pago por lei. Aparece como tile separado
 * pra evitar que o CFO interprete o custo total como passível de recuperação.
 */
function buildLeaveBlocksCostHighlight(
  result: DiagnosticResult,
): ExecutiveHighlight | null {
  const { costLeaveBlocks } = result.metrics.cost;
  if (costLeaveBlocks === null) return null;
  if (costLeaveBlocks.value <= 0) return null;

  const formattedValue = formatCurrencyBrl(costLeaveBlocks.value);
  const totalDays = result.metrics.absence.leaveBlocks.totalDays;
  const blocks = result.metrics.absence.leaveBlocks.count;

  return {
    code: 'cost.leave_blocks',
    label: 'Custo de afastamentos (informativo)',
    formattedValue,
    explanation: `${totalDays} dia${totalDays !== 1 ? 's' : ''} em ${blocks} bloco${blocks !== 1 ? 's' : ''} de atestado/licença/férias — pago por lei, não recuperável.`,
    assumption: 'informativo — não somado ao Custo Total',
    category: 'cost',
  };
}

function buildReworkHighlight(result: DiagnosticResult): ExecutiveHighlight | null {
  const { adjustmentRate } = result.metrics.rework;
  if (adjustmentRate.value <= 0) return null;

  return {
    code: 'rework.adjustment_rate',
    label: 'Taxa de marcações manuais',
    formattedValue: `${formatPercentage(adjustmentRate.value)} de marcações manuais`,
    explanation: `${formatPercentage(adjustmentRate.value)} das marcações de ponto foram registradas manualmente, indicando retrabalho operacional.`,
    category: 'rework',
  };
}

function buildRiskHighlight(result: DiagnosticResult): ExecutiveHighlight | null {
  const { risk } = result.metrics;
  const allSignals = [
    ...risk.inconsistentPairs,
    ...risk.excessiveJourneys,
    ...risk.shortIntradayBreaks,
    ...risk.shortInterjourneyBreaks,
    ...risk.potentialBritishPoint,
    ...risk.documentIntegrity,
  ];

  // Prioriza sinais de alta confiança, depois média
  const highSignals = allSignals.filter((s) => s.confidence === 'high');
  const mediumSignals = allSignals.filter((s) => s.confidence === 'medium');

  const topSignal = highSignals[0] ?? mediumSignals[0] ?? allSignals[0] ?? null;
  if (!topSignal) return null;

  return {
    code: `risk.top_signal`,
    label: 'Sinal de risco mais relevante',
    formattedValue: `${topSignal.occurrenceCount} ocorrência${topSignal.occurrenceCount !== 1 ? 's' : ''}`,
    explanation: topSignal.message.length <= 120
      ? topSignal.message
      : topSignal.message.slice(0, 117) + '...',
    category: 'risk',
  };
}

function buildVisibilityHighlight(result: DiagnosticResult): ExecutiveHighlight | null {
  const { completeJourneys, pendingJourneys } = result.metrics.visibility;
  const total = completeJourneys.value + pendingJourneys.value;
  if (total <= 0) return null;

  const completeRate = (completeJourneys.value / total) * 100;
  if (completeRate <= 0) return null;

  return {
    code: 'visibility.complete_journeys',
    label: 'Jornadas completas',
    formattedValue: `${formatPercentage(completeRate)} de jornadas completas`,
    explanation: `${completeJourneys.value} de ${total} jornadas analisadas possuem registros completos de entrada e saída.`,
    category: 'visibility',
  };
}

// =========================================================================
// Função principal
// =========================================================================

/**
 * Seleciona até 6 highlights executivos priorizando:
 * score → custo total → custo/colab/mês → custo afastamento (informativo)
 * → retrabalho → risco → visibilidade.
 *
 * Pula custo financeiro se MoneyEstimate é null.
 * Pula custo informativo de afastamentos quando não há blocos detectados.
 *
 * Limite default = 5; quando há leave-blocks e a empresa preenche premissas
 * monetárias, sobe para 6 — o tile informativo é crítico pra evitar que o
 * CFO interprete o custo total como passível de recuperação total.
 */
export function selectExecutiveHighlights(result: DiagnosticResult): ExecutiveHighlight[] {
  const candidates: ExecutiveHighlight[] = [];

  // 1. Score sempre entra (prioridade máxima)
  candidates.push(buildScoreHighlight(result));

  // 2. Custo total (somente se estimatedExcessCost não é null)
  const costHighlight = buildCostHighlight(result);
  if (costHighlight) candidates.push(costHighlight);

  // 3. Custo médio mensal por colaborador — número mais acionável pra
  //    consultor (R$/colaborador/mês é o que o gestor entende imediatamente).
  const costPerEmployeeHighlight = buildCostPerEmployeeHighlight(result);
  if (costPerEmployeeHighlight) candidates.push(costPerEmployeeHighlight);

  // 4. Custo informativo de afastamentos (atestado/licença/férias). Aparece
  //    DEPOIS dos custos recuperáveis pra deixar claro que é referência, não
  //    passível de revisão.
  const leaveBlocksHighlight = buildLeaveBlocksCostHighlight(result);
  if (leaveBlocksHighlight) candidates.push(leaveBlocksHighlight);

  // 5. Retrabalho
  const reworkHighlight = buildReworkHighlight(result);
  if (reworkHighlight) candidates.push(reworkHighlight);

  // 6. Risco
  const riskHighlight = buildRiskHighlight(result);
  if (riskHighlight) candidates.push(riskHighlight);

  // 7. Visibilidade
  const visibilityHighlight = buildVisibilityHighlight(result);
  if (visibilityHighlight) candidates.push(visibilityHighlight);

  // Ordena por prioridade decrescente. Sort estável preserva ordem de
  // inserção dentro da mesma categoria (cost.total → cost.per_employee →
  // cost.leave_blocks). Limite passa pra 6 quando há leave-blocks pra
  // garantir que o tile informativo aparece no resumo executivo.
  const limit = leaveBlocksHighlight ? 6 : 5;
  return candidates
    .sort((a, b) => highlightPriority(b) - highlightPriority(a))
    .slice(0, limit);
}
